/**
 * Basamento — scelta OBBLIGATORIA fra le varianti di un prodotto abbinato
 *
 * Le card (una per variante, renderizzate in Liquid da snippets/basamento.liquid)
 * funzionano a radio: se ne può scegliere una sola e non si può tornare a
 * "nessuna". Finché non c'è una scelta l'aggiunta al carrello è bloccata.
 *
 * - Prezzo: a ogni cambio pubblica `basamento:update` ({ price, blocked });
 *   configurator.js lo somma al prezzo mostrato e tiene disabilitato il
 *   pulsante finché `blocked` è true. Lo stato corrente resta anche in
 *   window.__bongioBasamentoState per il configuratore che si inizializza dopo.
 * - Carrello: su `variant:add` aggiunge la variante scelta come riga abbinata
 *   { _abbinato_a: <titolo padre>, _tipo_abbinato: 'Basamento',
 *     _sku_final: <SKU variante>, _ambiente? }. La quantità è poi allineata al
 *   padre dalla riconciliazione generica in quote-manager.js.
 * - Rete di sicurezza: un submit verso /cart/add senza scelta (es. dalla barra
 *   sticky, che il configuratore non governa) viene fermato.
 *
 * @version 1.0.0
 */

(function() {
  'use strict';

  // Singleton: la barra sticky (floated-form) clona i blocchi della scheda,
  // script compreso. Vedi la stessa guardia in corpo-incasso.js / set.js.
  if (window.__bongioBasamentoInit) return;
  window.__bongioBasamentoInit = true;

  const PRODUCT_FORM = 'form[action*="/cart/add"]';

  let section = null;
  let selected = null; // { variantId, price, sku, title, card }
  let isAdding = false;

  function init() {
    // Prima istanza fuori dalla barra sticky: le copie clonate restano nascoste.
    section = Array.prototype.find.call(
      document.querySelectorAll('[data-basamento]'),
      el => !el.closest('floated-form')
    );
    if (!section) return;
    section.style.display = 'block';

    if (section.dataset.selectable === 'false') return;

    section.querySelectorAll('.corpo-incasso__card[data-variant-id]').forEach(card => {
      card.addEventListener('click', () => select(card));
    });

    cleanupFormProperties();
    document.addEventListener('submit', onSubmitCapture, true);
    document.addEventListener('variant:add', onVariantAdd);
    publish();
  }

  // ==========================================================================
  // SELEZIONE
  // ==========================================================================

  function select(card) {
    section.querySelectorAll('.corpo-incasso__card').forEach(c => {
      const on = c === card;
      c.classList.toggle('corpo-incasso__card--selected', on);
      c.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    selected = {
      variantId: Number(card.dataset.variantId),
      price: Number(card.dataset.price) || 0,
      sku: card.dataset.sku || '',
      title: card.dataset.title || '',
      card
    };
    section.classList.add('basamento--chosen');
    section.classList.remove('basamento--alert');
    updateFormInputs();
    publish();
  }

  function publish() {
    const state = { price: selected ? selected.price : 0, blocked: !selected };
    window.__bongioBasamentoState = state;
    document.dispatchEvent(new CustomEvent('basamento:update', { detail: state }));
  }

  // Property visibile sulla riga del padre: separa in carrello lo stesso
  // articolo con pietre diverse (altrimenti Shopify fonderebbe le righe e la
  // riga abbinata seguirebbe la quantità sbagliata).
  function updateFormInputs() {
    document.querySelectorAll(PRODUCT_FORM).forEach(form => {
      form.querySelectorAll('input[name="properties[Basamento]"]').forEach(el => el.remove());
      if (!selected) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Basamento]';
      input.value = selected.title + ' (' + selected.sku + ')';
      form.appendChild(input);
    });
  }

  function cleanupFormProperties() {
    document.querySelectorAll('input[name="properties[Basamento]"]').forEach(el => el.remove());
  }

  function onSubmitCapture(e) {
    const form = e.target;
    if (!form || !form.matches || !form.matches(PRODUCT_FORM)) return;
    // Ricrea la property qui: clearHiddenInputs() del configuratore svuota e
    // disabilita TUTTE le properties del form a ogni selezione incompleta.
    if (selected) { updateFormInputs(); return; }
    e.preventDefault();
    e.stopImmediatePropagation();
    section.classList.add('basamento--alert');
    section.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ==========================================================================
  // CARRELLO
  // ==========================================================================

  async function onVariantAdd() {
    if (!selected || isAdding) return;
    isAdding = true;

    try {
      const variantId = selected.variantId;
      const abbinatoA = document.querySelector('.product__title')?.textContent?.trim() || '';
      let amb = '';
      try { amb = localStorage.getItem('bongio:ambiente_attivo') || ''; } catch (_) {}

      // Dedupe su (variante + padre + ambiente), come corpo-incasso.js.
      try {
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          const dup = (cart.items || []).some(it =>
            it.variant_id === variantId &&
            it.properties &&
            String(it.properties._abbinato_a || '') === abbinatoA &&
            String(it.properties._ambiente || '') === amb
          );
          if (dup) { refreshCart(); return; }
        }
      } catch (e) {
        console.warn('[Basamento] dedupe check failed, proceeding', e);
      }

      const properties = { '_abbinato_a': abbinatoA, '_tipo_abbinato': 'Basamento' };
      if (selected.sku) properties._sku_final = selected.sku;
      if (amb) properties._ambiente = amb;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties }] })
      });
      const data = await res.json().catch(() => ({}));
      if (data.status) {
        console.warn('[Basamento] Failed to add:', data.description);
        return;
      }
      refreshCart();
    } catch (err) {
      console.error('[Basamento] Add to cart error:', err);
    } finally {
      isAdding = false;
    }
  }

  function refreshCart() {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (cart && cart.getSectionsToRender) {
      const sections = cart.getSectionsToRender();
      fetch(`${window.location.pathname}?sections=${sections.map(s => s.id).join(',')}`)
        .then(res => res.json())
        .then(html => {
          sections.forEach(s => {
            const el = document.getElementById(s.id);
            if (!el || !html[s.id]) return;
            el.innerHTML = new DOMParser()
              .parseFromString(html[s.id], 'text/html')
              .getElementById(s.id)?.innerHTML || html[s.id];
          });
        })
        .catch(err => console.warn('[Basamento] Cart refresh error:', err));
    }

    fetch('/cart.js')
      .then(res => res.json())
      .then(c => {
        document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(el => { el.textContent = c.item_count; });
      })
      .catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
